/**
 * QueueModes — the shared MATCHMAKING QUEUE mode specification.
 *
 * There are TWO independent matchmaking queues:
 *  - 'fast': a FAST game — small match, short wait (QUEUE_MODE_PLAYER_COUNTS).
 *  - 'long': a LONG game — bigger match, longer wait.
 *
 * A player can only ever be in ONE queue at a time (the server rejects a
 * join while the player is already queued in either queue — switching
 * requires leaving first).
 *
 * Shared by EVERY site: the server (MatchService queues, DevServer join
 * handler + queue status broadcast), the client (the queue-mode picker on
 * the Join Queue button and the per-queue player counts in the queue
 * view), and the tests. APPEND a new mode by adding it to QUEUE_MODES +
 * the two Record tables below — nothing else to wire.
 */

import { GameConfig } from '../../config/gameConfig';

/** A matchmaking queue mode. */
export type QueueMode = 'fast' | 'long';

/** All queue modes (order = display order in the mode picker). */
export const QUEUE_MODES: readonly QueueMode[] = ['fast', 'long'];

/** The number of players needed to start a match in each queue. */
export const QUEUE_MODE_PLAYER_COUNTS: Readonly<Record<QueueMode, number>> = {
  fast: 4,
  long: 8,
};

/** Human-readable labels for the mode picker and queue view. */
export const QUEUE_MODE_LABELS: Readonly<Record<QueueMode, string>> = {
  fast: 'Fast Game',
  long: 'Long Game',
};

/** Short tag shown next to a player's queue in the queue view. */
export const QUEUE_MODE_SHORT: Readonly<Record<QueueMode, string>> = {
  fast: 'Fast',
  long: 'Long',
};

/**
 * Type guard for a queue mode value (wire messages / request validation).
 */
export function isQueueMode(value: unknown): value is QueueMode {
  return value === 'fast' || value === 'long';
}

/**
 * The player count needed to start a match in the given queue mode.
 */
export function playerCountForQueueMode(mode: QueueMode): number {
  return QUEUE_MODE_PLAYER_COUNTS[mode];
}

/**
 * Long games start everyone with 6 life instead of the standard 5
 * (GameConfig.startingLife) — the 8-player match runs longer and the
 * extra point is the forgiveness for it. Fast games keep the standard 5.
 */
export const LONG_GAME_STARTING_LIFE = 6;

/**
 * The starting life for a match created from the given queue mode.
 */
export function startingLifeForQueueMode(mode: QueueMode): number {
  return mode === 'long' ? LONG_GAME_STARTING_LIFE : GameConfig.startingLife;
}
